/**
 * Tree Node Tools - URL manipulation and tree node toggle functions
 * Provides utilities for manipulating URLs and toggling tree node states.
 */

// Declare external dependencies

// Module flag
const _TREENODETOOLS_JS_ = true;

// Types
interface FormObjInterface {
  getElement: (name: string) => HTMLElement | null;
}

/**
 * Toggle a tree node's expanded/collapsed state.
 * Updates the URL with the toggle action and either navigates or submits the form.
 * @param action - The toggle action (e.g., 'expand', 'collapse')
 * @param id - The node ID
 * @param form - Optional form name to submit instead of navigating
 * @param url - Optional URL to use (defaults to current location)
 */
function toggleNode(action: string, id: string, form?: string | null, url?: string | null): void {
  if (url == null || url.length == 0) {
    url = removeAnchor(window.location.href);
  }
  url = removeURLParam(url, '__t');
  url = addURLParam(url, '__t', action + ',' + id);
  url = addScrollPos(url);
  if (form == null || form.length == 0) {
    window.location.href = url;
  } else {
    var formObj = getFormObj(form);
    if (formObj.getElement("_postEvent") == null && formObj.getElement("_event") == null) {
      window.location.href = url;
    } else {
      url = removeURLParam(url, '_event');
      disableValidation();
      submitForm(form, 'persist', url);
    }
  }
}

/**
 * Add scroll position parameter to URL.
 * @param url - The URL to modify
 * @returns URL with scroll position parameter added
 */
function addScrollPos(url: string): string {
  url = removeURLParam(removeAnchor(url), '__scrollPos');
  return addURLParam(url, '__scrollPos', pagePosTop());
}

/**
 * Remove the anchor/hash portion from a URL.
 * @param url - The URL to process
 * @returns URL without anchor
 */
function removeAnchor(url: string): string {
  var p = url.indexOf('#');
  return p == -1 ? url : url.substring(0, p);
}

/**
 * Get the anchor/hash portion from a URL.
 * @param url - The URL to process
 * @returns The anchor portion (including #) or empty string
 */
function getAnchor(url: string): string {
  var p = url.indexOf('#');
  return p == -1 ? "" : url.substring(p);
}

/**
 * Add a parameter to a URL.
 * @param url - The URL to modify
 * @param p - The parameter name
 * @param v - The parameter value
 * @returns URL with parameter added
 */
function addURLParam(url: string, p: string, v: string | number): string {
  var anchor = getAnchor(url);
  url = removeAnchor(url);
  url += (url.indexOf('?') == -1 ? '?' : '&') + p + '=' + v;
  url += anchor;
  return url;
}

/**
 * Remove a parameter from a URL.
 * @param url - The URL to modify
 * @param p - The parameter name to remove
 * @returns URL with parameter removed
 */
function removeURLParam(url: string, p: string): string {
  var anchor = getAnchor(url);
  url = removeAnchor(url);
  var s = url.indexOf('&' + p + '=');
  if (s == -1) s = url.indexOf('?' + p + '=');
  if (s != -1) {
    var e = url.indexOf('&', s + 1);
    url = e != -1 ? url.substring(0, s + 1) + url.substring(e + 1) : url.substring(0, s);
  }
  url += anchor;
  return url;
}

// Export for module usage
export {
  _TREENODETOOLS_JS_,
  toggleNode,
  addScrollPos,
  removeAnchor,
  getAnchor,
  addURLParam,
  removeURLParam
};

// Export types
export type { FormObjInterface };

// Expose on globalThis for backwards compatibility
(globalThis as any)._TREENODETOOLS_JS_ = _TREENODETOOLS_JS_;
(globalThis as any).toggleNode = toggleNode;
(globalThis as any).addScrollPos = addScrollPos;
(globalThis as any).removeAnchor = removeAnchor;
(globalThis as any).getAnchor = getAnchor;
(globalThis as any).addURLParam = addURLParam;
(globalThis as any).removeURLParam = removeURLParam;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("treenodetools.js");
}
